import { Shield, CheckCircle, CreditCard, Headphones, Lock } from "lucide-react";
import { useCountryConfig } from "@/lib/countryConfig";

const features = [
  {
    icon: Shield,
    title: "ID Verified & Background Checked",
    badge: "100% Verified",
    badgeColor: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    desc: "Every helper undergoes comprehensive identity verification and background screening before joining.",
    iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-emerald-100 dark:border-emerald-800/40",
  },
  {
    icon: CheckCircle,
    title: "Fair Issue Resolution",
    badge: "Buyer Protection",
    badgeColor: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    desc: "Not happy with the service? Report it from your booking and our team will review it fairly, with a refund where one is due.",
    iconBg: "bg-blue-50 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-100 dark:border-blue-800/40",
  },
  {
    icon: CreditCard,
    title: "Secure Cashless Payments",
    badge: "Bank-Level Security",
    badgeColor: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    desc: "Pay via MTN MoMo, Airtel Money, Visa, or Mastercard. Your money is protected end to end.",
    iconBg: "bg-amber-50 dark:bg-amber-900/20",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-100 dark:border-amber-800/40",
  },
  {
    icon: Headphones,
    title: "24-Hour Chat Support",
    badge: "Always Available",
    badgeColor: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    desc: "Chat with us any time, day or night, from the help button. Our call centre is open 8am to 6pm.",
    iconBg: "bg-rose-50 dark:bg-rose-900/20",
    iconColor: "text-rose-600 dark:text-rose-400",
    borderColor: "border-rose-100 dark:border-rose-800/40",
  },
];

export function TrustSafety() {
  const { config } = useCountryConfig();
  return (
    <section className="py-10 sm:py-16 bg-background">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 rounded-full px-4 py-1.5 mb-4">
            <Lock className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-emerald-700 dark:text-emerald-400 text-xs font-semibold tracking-wide uppercase">Trust & Safety</span>
          </div>
          <h2 className="h-page text-foreground mb-2">
            Your Trust & Safety Matter
          </h2>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Comprehensive safety measures so you can book with complete confidence.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((feature) => (
            <div
              key={feature.title}
              className={`bg-card rounded-2xl p-5 border ${feature.borderColor} shadow-sm hover:shadow-md transition-all duration-200 flex gap-4`}
            >
              <div className={`${feature.iconBg} rounded-xl p-3 flex-shrink-0 h-fit`}>
                <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start gap-2 mb-1.5">
                  <h3 className="font-bold text-foreground text-sm leading-tight">{feature.title}</h3>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${feature.badgeColor} flex-shrink-0`}>
                    {feature.badge}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-8">
          {[
            { label: `${config.legal.regulator} Licensed` },
            { label: "NITA-U Compliant" },
            { label: "URSB Registered" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-muted-foreground">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <span className="text-xs">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
